import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { History, Coins, ArrowUpRight, ArrowDownLeft, Filter, Search, Loader2, Tv } from 'lucide-react'
import useBetStore from '../store/useBetStore'
import Card from '../components/ui/Card'
import Skeleton from '../components/ui/Skeleton'

export default function TransactionsSection() {
  const { credits, transactions, isLoading, fetchBalance } = useBetStore()
  const [filterType, setFilterType] = useState('all') // 'all', 'recarga', 'apuesta', 'premio', 'devolucion', 'retiro'
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchBalance().catch(() => {})
  }, [])

  // Filtrar transacciones localmente en base a filtros de tipo y búsqueda de texto (referencia)
  const filteredTransactions = transactions.filter(t => {
    const matchesType = filterType === 'all' || t.tipo === filterType
    const matchesSearch = !searchTerm.trim() || 
      (t.referencia && t.referencia.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesType && matchesSearch
  })

  // Obtener estilos y etiquetas legibles por tipo
  const getBadgeStyles = (tipo) => {
    switch (tipo) {
      case 'recarga':
        return { label: 'Recarga', bg: 'bg-green-500/10 text-green-400 border-green-500/25', icon: ArrowDownLeft }
      case 'apuesta':
        return { label: 'Apuesta', bg: 'bg-orange-500/10 text-orange-400 border-orange-500/25', icon: ArrowUpRight }
      case 'premio':
        return { label: 'Premio', bg: 'bg-cyan/15 text-cyan border-cyan/25', icon: ArrowDownLeft }
      case 'devolucion':
        return { label: 'Devolución', bg: 'bg-border-default/15 text-text-secondary border-border-strong/20', icon: ArrowDownLeft }
      case 'retiro':
        return { label: 'Retiro', bg: 'bg-error/10 text-error border-error/25', icon: ArrowUpRight }
      case 'transmision':
        return { label: 'Transmisión', bg: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/25', icon: Tv }
      default:
        return { label: tipo, bg: 'bg-navy-dark border-border-subtle text-text-muted', icon: ArrowUpRight }
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-2">
            <History className="w-8 h-8 text-cyan animate-float" /> Historial de Movimientos
          </h1>
          <p className="text-text-secondary mt-1">
            Revisa el libro contable de tus créditos: depósitos, apuestas, cobro de premios y retiros.
          </p>
        </div>

        {/* Saldo rápido */}
        <div className="glass px-6 py-3 rounded-2xl border border-border-subtle flex items-center gap-4 self-start md:self-auto">
          <div>
            <p className="text-text-muted text-xs font-medium uppercase tracking-wider">Saldo Disponible</p>
            <p className="text-xl font-display font-bold text-cyan">{credits.toFixed(2)} Bs</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-cyan/15 flex items-center justify-center text-cyan">
            <Coins className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="grid sm:grid-cols-3 gap-4 bg-surface-card/40 border border-border-subtle p-4 rounded-2xl glass">
        {/* Búsqueda */}
        <div className="relative col-span-2">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Buscar por referencia o detalle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-navy-dark/45 border border-border-subtle rounded-xl pl-10 pr-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan text-sm transition-colors"
          />
        </div>

        {/* Selector de Tipo */}
        <div className="relative">
          <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full bg-navy-dark/45 border border-border-subtle rounded-xl pl-10 pr-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan text-sm transition-colors cursor-pointer appearance-none"
          >
            <option value="all">Todos los tipos</option>
            <option value="recarga">Recargas</option>
            <option value="apuesta">Apuestas</option>
            <option value="premio">Premios</option>
            <option value="devolucion">Devoluciones</option>
            <option value="retiro">Retiros</option>
          </select>
        </div>
      </div>

      {/* Listado Principal */}
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      ) : filteredTransactions.length === 0 ? (
        <Card className="p-12 text-center text-text-muted border border-border-subtle bg-surface-card/30">
          <History className="w-12 h-12 mx-auto mb-3 opacity-40 text-cyan" />
          <h3 className="font-display font-semibold text-lg text-text-primary mb-1">Sin movimientos</h3>
          <p className="text-sm">
            No se encontraron transacciones que coincidan con los filtros seleccionados.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTransactions.map((t, index) => {
            const montoVal = parseFloat(t.monto)
            const esPositivo = montoVal >= 0
            const badge = getBadgeStyles(t.tipo)
            
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(index * 0.05, 0.4) }}
                className="bg-surface-card/40 hover:bg-surface-card/65 border border-border-subtle rounded-2xl p-4 flex items-center justify-between gap-4 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  {/* Ícono de Entrada/Salida */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                    esPositivo 
                      ? 'bg-success/10 border-success/20 text-success' 
                      : 'bg-error/10 border-error/20 text-error'
                  }`}>
                    <badge.icon className="w-5 h-5" />
                  </div>

                  <div className="space-y-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border capitalize ${badge.bg}`}>
                        {badge.label}
                      </span>
                      {t.estado && (
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                          t.estado === 'pendiente' 
                            ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' 
                            : t.estado === 'aprobado'
                            ? 'bg-green-500/10 text-green-400 border-green-500/25'
                            : 'bg-red-500/10 text-red-400 border-red-500/25'
                        }`}>
                          {t.estado === 'pendiente' ? 'Pendiente' : (t.estado === 'aprobado' ? 'Aprobado' : 'Rechazado')}
                        </span>
                      )}
                      <span className="text-[10px] text-text-muted font-mono">
                        ID #{t.id}
                      </span>
                      <span className="text-[10px] text-text-muted">
                        • {new Date(t.creado_en).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-text-primary truncate font-body">
                      {t.referencia || 'Sin descripción'}
                    </p>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <span className={`font-display font-bold text-base md:text-lg ${
                    esPositivo ? 'text-success' : 'text-error'
                  }`}>
                    {esPositivo ? '+' : ''}
                    {montoVal.toFixed(2)} Bs
                  </span>
                </div>
              </motion.div>
            )
          })}
        </div>
      )}
    </div>
  )
}
